import type { ManifestPage, MediaDescriptor } from "@aasaan-share/protocol";

export type RenditionName = "thumbnail" | "preview";

export function galleryRendition(descriptor: MediaDescriptor): RenditionName {
  return descriptor.kind === "image" && descriptor.renditions.preview
    ? "preview"
    : "thumbnail";
}

export interface GalleryMedia {
  readonly descriptor: MediaDescriptor;
  readonly previewUrl?: string;
  readonly thumbnailUrl?: string;
}

export interface ObjectUrlApi {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
}

const browserObjectUrls: ObjectUrlApi = {
  createObjectURL: (blob) => URL.createObjectURL(blob),
  revokeObjectURL: (url) => URL.revokeObjectURL(url),
};

export class GallerySession {
  static readonly MAX_PREVIEW_URLS = 96;
  static readonly MAX_THUMBNAIL_URLS = 96;
  readonly #media = new Map<string, MediaDescriptor>();
  readonly #previewUrls = new Map<string, string>();
  readonly #thumbnailUrls = new Map<string, string>();
  readonly #objectUrls: ObjectUrlApi;
  #hasMore = true;
  #nextCursor: string | null = null;
  #pendingMedia: Map<string, MediaDescriptor> | null = null;
  #pendingNextCursor: string | null = null;
  #pendingRevision: string | null = null;
  #pendingTotalMediaCount: number | undefined;
  #revision: string | null = null;
  #scrollTop = 0;
  #totalMediaCount: number | undefined;

  constructor(
    readonly shareId: string,
    objectUrls: ObjectUrlApi = browserObjectUrls,
  ) {
    this.#objectUrls = objectUrls;
  }

  get revision(): string | null {
    return this.#revision;
  }

  get nextCursor(): string | null {
    return this.#pendingRevision === null
      ? this.#nextCursor
      : this.#pendingNextCursor;
  }

  get hasMore(): boolean {
    return this.#pendingRevision === null
      ? this.#hasMore
      : this.#pendingNextCursor !== null;
  }

  get totalMediaCount(): number | undefined {
    return this.#totalMediaCount;
  }

  get scrollTop(): number {
    return this.#scrollTop;
  }

  set scrollTop(value: number) {
    this.#scrollTop = Math.max(0, value);
  }

  items(): readonly GalleryMedia[] {
    return [...this.#media.values()].map((descriptor) => ({
      descriptor,
      previewUrl: this.#previewUrls.get(descriptor.mediaId),
      thumbnailUrl: this.#thumbnailUrls.get(descriptor.mediaId),
    }));
  }

  descriptor(mediaId: string): MediaDescriptor | undefined {
    return this.#media.get(mediaId);
  }

  applyPage(page: ManifestPage): boolean {
    if (page.shareId !== this.shareId) {
      throw new Error("Manifest page belongs to another share");
    }

    if (this.#revision !== null && this.#revision !== page.revision) {
      return this.applyRefreshPage(page);
    }
    for (const descriptor of page.media) {
      this.#media.set(descriptor.mediaId, descriptor);
    }
    this.#revision = page.revision;
    this.#nextCursor = page.nextCursor;
    this.#hasMore = page.nextCursor !== null;
    if (page.totalMediaCount !== undefined) {
      this.#totalMediaCount = page.totalMediaCount;
    }
    return true;
  }

  setObjectUrl(
    mediaId: string,
    rendition: RenditionName,
    blob: Blob,
  ): string {
    if (!this.#media.has(mediaId)) {
      throw new Error("Cannot cache media outside this share");
    }
    const urls = rendition === "thumbnail"
      ? this.#thumbnailUrls
      : this.#previewUrls;
    const previous = urls.get(mediaId);
    if (previous) {
      this.#objectUrls.revokeObjectURL(previous);
      urls.delete(mediaId);
    }
    const url = this.#objectUrls.createObjectURL(blob);
    urls.set(mediaId, url);
    const maximum = rendition === "thumbnail"
      ? GallerySession.MAX_THUMBNAIL_URLS
      : GallerySession.MAX_PREVIEW_URLS;
    while (urls.size > maximum) {
      const oldestId = urls.keys().next().value;
      if (!oldestId) break;
      this.revokeUrl(urls, oldestId);
    }
    return url;
  }

  removeMedia(mediaId: string): void {
    this.revokeUrl(this.#thumbnailUrls, mediaId);
    this.revokeUrl(this.#previewUrls, mediaId);
    this.#media.delete(mediaId);
  }

  removeVideos(): void {
    for (const descriptor of this.#media.values()) {
      if (descriptor.kind === "video") this.removeMedia(descriptor.mediaId);
    }
  }

  dispose(): void {
    this.clearMedia();
    this.#revision = null;
    this.#nextCursor = null;
    this.#pendingMedia = null;
    this.#pendingNextCursor = null;
    this.#pendingRevision = null;
    this.#pendingTotalMediaCount = undefined;
    this.#hasMore = true;
    this.#totalMediaCount = undefined;
  }

  private applyRefreshPage(page: ManifestPage): boolean {
    if (this.#pendingRevision !== page.revision) {
      this.#pendingMedia = new Map();
      this.#pendingNextCursor = null;
      this.#pendingRevision = page.revision;
      this.#pendingTotalMediaCount = undefined;
    }
    for (const descriptor of page.media) {
      this.#pendingMedia!.set(descriptor.mediaId, descriptor);
    }
    this.#pendingNextCursor = page.nextCursor;
    if (page.totalMediaCount !== undefined) {
      this.#pendingTotalMediaCount = page.totalMediaCount;
    }
    if (page.nextCursor !== null) return false;

    const media = this.#pendingMedia!;
    this.retainUnchangedUrls(media);
    this.#media.clear();
    for (const [mediaId, descriptor] of media) {
      this.#media.set(mediaId, descriptor);
    }
    this.#revision = page.revision;
    this.#nextCursor = null;
    this.#hasMore = false;
    this.#totalMediaCount = this.#pendingTotalMediaCount;
    this.#pendingMedia = null;
    this.#pendingNextCursor = null;
    this.#pendingRevision = null;
    this.#pendingTotalMediaCount = undefined;
    return true;
  }

  private retainUnchangedUrls(nextMedia: ReadonlyMap<string, MediaDescriptor>): void {
    for (const [mediaId, descriptor] of this.#media) {
      const next = nextMedia.get(mediaId);
      if (
        !next
        || JSON.stringify(next) !== JSON.stringify(descriptor)
      ) {
        this.revokeUrl(this.#thumbnailUrls, mediaId);
        this.revokeUrl(this.#previewUrls, mediaId);
      }
    }
  }

  private clearMedia(): void {
    for (const url of this.#thumbnailUrls.values()) {
      this.#objectUrls.revokeObjectURL(url);
    }
    for (const url of this.#previewUrls.values()) {
      this.#objectUrls.revokeObjectURL(url);
    }
    this.#thumbnailUrls.clear();
    this.#previewUrls.clear();
    this.#media.clear();
  }

  private revokeUrl(urls: Map<string, string>, mediaId: string): void {
    const url = urls.get(mediaId);
    if (url) this.#objectUrls.revokeObjectURL(url);
    urls.delete(mediaId);
  }
}
